import { useCallback, useMemo, useState, useEffect } from 'react';
import { isCancel } from 'axios';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { useUserStore } from '@/state/user.state';
import { canCreditsRollover, getMonthlyAllocatedCredits, getRenewalDate } from '@/utils/subscriptions.utils';
import { getCustomerPortalSession } from '@/api/workspaceSettings';
import { log } from '@/logger/logger';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';

const logger = log.getLogger('WorkspacePlanSettings');

export const useSubscriptionPlan = () => {
  const { t } = useTranslation();
  const { managePaymentsPermissions } = useSubscriptionPermissions();
  const [customerPortalSession, setCustomerPortalSession] = useState('');
  const activeWorkspace = useUserStore((state) => state.user?.activeWorkspace);
  const subscription = activeWorkspace?.subscription;
  const workspaceId = activeWorkspace?.workspaceId;
  const planName = subscription?.name ?? '';
  const usedSeats = String(subscription?.usedSeats ?? '');
  const seats = String(subscription?.seatsLimit ?? '');
  const monthlyAllocatedCredits = getMonthlyAllocatedCredits(subscription);
  const creditsCanRollover = canCreditsRollover(subscription);

  const date = useMemo(() => {
    return subscription?.scheduledDowngradeDate ? subscription?.scheduledDowngradeDate : getRenewalDate(subscription);
  }, [subscription]);

  const dateLabel = useMemo(() => {
    return subscription?.scheduledDowngradeDate
      ? t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.EXPIRATION_DATE)
      : t(I18N_KEYS.SETTINGS.WORKSPACE_SETTINGS.PLAN.RENEWAL_DATE);
  }, [subscription?.scheduledDowngradeDate, t]);

  const fetchCustomerPortalSession = useCallback(
    async (signal: AbortSignal) => {
      if (!workspaceId) return;
      try {
        const link = await getCustomerPortalSession({
          workspaceId,
          returnUrl: window.location.href,
          signal,
        });
        setCustomerPortalSession(link);
      } catch (e) {
        if (isCancel(e)) {
          logger.log('Request was cancelled');
        }
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    const abortController = new AbortController();
    if (managePaymentsPermissions.action) {
      void fetchCustomerPortalSession(abortController.signal);
    }
    return () => {
      abortController.abort();
    };
  }, [fetchCustomerPortalSession, managePaymentsPermissions?.action]);

  return {
    customerPortalSession,
    planName,
    usedSeats,
    seats,
    monthlyAllocatedCredits,
    creditsCanRollover,
    date,
    dateLabel,
    scheduledDowngradePlan: subscription?.scheduledDowngradePlan,
    scheduledDowngradeDate: subscription?.scheduledDowngradeDate,
    subscriptionType: subscription?.type,
  };
};
